import {
  Business,
  DashboardCustomizeOutlined,
  DatasetLinkedOutlined,
  EditOutlined,
  InsertDriveFileOutlined,
  ShoppingBagOutlined,
  ShoppingCartOutlined,
  SpaceDashboardOutlined,
  WidgetsOutlined,
} from '@mui/icons-material';
import { Divider, List } from '@mui/material';
import { Permission } from '@procureai/permissions';
import { usePermissions, Restricted } from '@procureai/permissions-ui';
import { NavCollapse, NavDrawerProps } from '@procureai/ui-mui';
import { Trans, useTranslation } from 'react-i18next';

import { AstroSmartLinkDomain } from '@/generated/gql/graphql';
import { BaseDomainType, I18nNamespace, NavId, FeatureFlag } from '~/common/types';
import { NavDrawerSimple } from '~/components/NavDrawerSimple/NavDrawerSimple';
import { NavSubListItemLink } from '~/components/NavSubListItemLink/NavSubListItemLink';
import { SmartLinksEditDialog } from '~/components/SmartLinksEditDialog/SmartLinksEditDialog';
import { SmartLinksNavList } from '~/components/SmartLinksNavList/SmartLinksNavList';
import { RouteId } from '~/routes/RouteId';

export const AnalyticsDrawer = (props: NavDrawerProps) => {
  const { hasPermission } = usePermissions();
  const { t } = useTranslation(I18nNamespace.common, { keyPrefix: 'Nav' });

  return (
    <NavDrawerSimple navId={NavId.analytics} {...props}>
      <List sx={{ marginBottom: 4 }}>
        <NavSubListItemLink
          id={`baseDomain.${BaseDomainType.GENERAL}`}
          to={RouteId.analyticsGeneral}
          Icon={SpaceDashboardOutlined}
          needs={{ permission: Permission.BaseGeneral }}
        />
      </List>
      {hasPermission({
        any: [
          Permission.BaseGeneral,
          Permission.BaseOrderList,
          Permission.BaseCommodityList,
          Permission.BaseContractList,
          Permission.BaseRequisitionList,
          Permission.BaseSupplierList,
          Permission.AnalyticsView,
        ],
      }) && (
        <NavCollapse in label={t(`baseDomain.sectionHeader`)} sx={{ marginBottom: 4 }}>
          <List sx={{ marginBottom: 0 }}>
            <NavSubListItemLink
              id={`baseDomain.${BaseDomainType.SUPPLIER}`}
              to={RouteId.analyticsBase(BaseDomainType.SUPPLIER)}
              Icon={Business}
              needs={{ permission: Permission.BaseSupplierList }}
            />
            <NavSubListItemLink
              id={`baseDomain.${BaseDomainType.COMMODITY_GROUP}`}
              to={RouteId.analyticsBase(BaseDomainType.COMMODITY_GROUP)}
              Icon={WidgetsOutlined}
              needs={{ permission: Permission.BaseCommodityGroupList }}
            />
            <NavSubListItemLink
              id={`baseDomain.${BaseDomainType.COMMODITY}`}
              to={RouteId.analyticsBase(BaseDomainType.COMMODITY)}
              Icon={WidgetsOutlined}
              needs={{ permission: Permission.BaseCommodityList }}
            />
            <NavSubListItemLink
              id={`baseDomain.${BaseDomainType.CONTRACT}`}
              to={RouteId.analyticsBase(BaseDomainType.CONTRACT)}
              Icon={InsertDriveFileOutlined}
              needs={{ permission: Permission.BaseContractList }}
            />
            <NavSubListItemLink
              id={`baseDomain.${BaseDomainType.ORDER}`}
              to={RouteId.analyticsBase(BaseDomainType.ORDER)}
              Icon={ShoppingCartOutlined}
              needs={{ permission: Permission.BaseOrderList }}
            />
            <NavSubListItemLink
              id={`baseDomain.${BaseDomainType.PURCHASE_REQUISITION}`}
              to={RouteId.analyticsBase(BaseDomainType.PURCHASE_REQUISITION)}
              Icon={ShoppingBagOutlined}
              needs={{ permission: Permission.BaseRequisitionList }}
            />
          </List>
        </NavCollapse>
      )}
      <Divider />
      <List sx={{ marginBottom: 4 }}>
        <NavSubListItemLink
          id={NavId.dashboardBuilder}
          to={RouteId.analyticsSuperset}
          Icon={DashboardCustomizeOutlined}
          needs={{ permission: Permission.AnalyticsView }}
        />
      </List>
      <Restricted needs={{ feature: FeatureFlag.SMART_LINKS }}>
        <SmartLinksNavList
          domain={AstroSmartLinkDomain.ANALYTICS}
          label={<Trans i18nKey={`Nav.${NavId.teamLinkedDashboards}`} />}
          Icon={DatasetLinkedOutlined}
        >
          <Restricted needs={{ permission: Permission.SmartLinkManage }}>
            <SmartLinksEditDialog
              domain={AstroSmartLinkDomain.ANALYTICS}
              label={NavId.teamManageDashboardLinks}
              dialogTitle={NavId.teamManageDashboardLinks}
              Icon={EditOutlined}
            />
          </Restricted>
        </SmartLinksNavList>
      </Restricted>
    </NavDrawerSimple>
  );
};
